import { useState } from 'react';
import { useTranslate } from 'react-polyglot';

import type { QuestionAnalytics } from '../../../../../../utils/buildQuestionAnalytics';
import { SectionTitle } from '../../../common/SectionTitle.styled';
import { UserResultsToggle } from '../../../common/UserResultsToggle';
import { QuestionUserFilter } from '../QuestionUserFilter/QuestionUserFilter';
import { QuestionUserRow } from '../QuestionUserRow/QuestionUserRow';
import { Header, List, ScrollArea, Wrapper } from './QuestionUserResults.styled';

/** Every row passes: the filter is off. */
const ALL = 'all';

interface QuestionUserResultsProps {
  analytics: QuestionAnalytics;
  /** Full screen bounds the column to the stage, so only there does the list scroll. */
  isExpanded?: boolean;
}

/**
 * Who answered what. The list has one row per participant of the session, so it can never
 * contradict the count on the cards or the silent slice of the chart, and it starts
 * covered: every entry into a view the host may be projecting begins anonymous.
 */
export const QuestionUserResults = ({
  analytics,
  isExpanded = false,
}: QuestionUserResultsProps) => {
  const t = useTranslate();

  const { kind, chart, rows } = analytics;

  // Autocorrective questions filter by outcome; everything else, by the option or value
  // its own chart already draws.
  const isScorable = kind === 'scorable';
  const bars = chart.kind === 'bars' ? chart.bars : [];

  // Deliberately not lifted: this section is mounted with the question's id as its key, so
  // moving to another question remounts it and both controls reset on their own.
  const [areNamesVisible, setAreNamesVisible] = useState(false);
  const [filter, setFilter] = useState<string>(ALL);

  const handleToggleNames = () => {
    setAreNamesVisible(previous => !previous);
  };

  const options = [
    { value: ALL, label: t('__new.play.analyticsResults.question.filter.all') },
    ...(isScorable
      ? [
          { value: 'correct', label: t('__new.play.analyticsResults.question.correct') },
          {
            value: 'incorrect',
            label: t('__new.play.analyticsResults.question.incorrect'),
          },
          {
            value: 'unanswered',
            label: t('__new.play.analyticsResults.question.unanswered'),
          },
        ]
      : bars.map(({ id, label, optionPosition }) => ({
          value: id,
          label:
            label ??
            t('__new.play.analyticsResults.option.unnamed', { position: optionPosition }),
        }))),
  ];

  const visibleRows = rows.filter(({ status, barIds }) => {
    if (filter === ALL) return true;

    return isScorable ? status === filter : barIds.includes(filter);
  });

  return (
    <Wrapper $isExpanded={isExpanded}>
      <UserResultsToggle
        label={t('__new.play.analyticsResults.question.showNames')}
        isChecked={areNamesVisible}
        onChange={handleToggleNames}
      />

      <Header $isBlurred={!areNamesVisible}>
        <SectionTitle>
          {t('__new.play.analyticsResults.question.participantsList')}
        </SectionTitle>

        <QuestionUserFilter options={options} value={filter} onChange={setFilter} />
      </Header>

      <ScrollArea $isExpanded={isExpanded}>
        <List $isBlurred={!areNamesVisible} data-cy="analytics-question-participants">
          {visibleRows.map(row => (
            <QuestionUserRow key={row.username} row={row} />
          ))}
        </List>
      </ScrollArea>
    </Wrapper>
  );
};
